import { AmpSettingsMap, ConnectorDto, ConnectorStatus, CredentialStatus, ProviderDto, UserCoverageSummary } from '@ampsec/platform-client';
import type { SideBarDef } from 'ag-grid-community';
import { agGridAmpDarkTheme } from 'Core-utils/agGridTheme';
import React from 'react';

export type ColumnsFn = (
    installedConnectorMap: { [cid: string]: ConnectorDto & { provider: ProviderDto } },
    providerNameIntegrationMap: { [providerName: string]: any },
    tenantSettings: AmpSettingsMap | undefined,
    isLoading: boolean,
    t: any,
    connectorDataHook: any,
) => Array<{
    field: string;
    headerName: string;
    headerTooltip?: string;
    resizable?: boolean;
    sortable?: boolean;
    filter?: string | boolean | any;
    filterParams?: any;
    minWidth?: number | undefined;
    cellRenderer?: (params: any) => React.ReactNode;
    valueGetter?: (params: any) => any;
    [key: string]: any;
}>;

import {
  COLLABORATION,
  ENDPOINT_MANAGEMENT,
  ENDPOINT_SECURITY,
  IDENTITY_AND_ACCESS,
  SECURITY_TRAINING,
  VULNERABILITY_MANAGEMENT
} from 'Core-utils/constants/constants';

export const userDomainToTypeMap: DomainToColumnMap = {
    [IDENTITY_AND_ACCESS]: { columnIcon: 'user' },
    [COLLABORATION]: { columnIcon: 'user' },
    [SECURITY_TRAINING]: { columnIcon: 'user' },
};

export const assetDomainToTypeMap: DomainToColumnMap = {
    [ENDPOINT_MANAGEMENT]: { columnIcon: 'asset' },
    [ENDPOINT_SECURITY]: { columnIcon: 'asset' },
    [VULNERABILITY_MANAGEMENT]: { columnIcon: 'asset', columnTitle: 'Vuln Management' },
};

export const getMinWidthForColumn = (column: string) => {
  return DEFAULT_COLUMN_STATE.find((c) => c.colId === column)?.width;
};

/** Default column order, widths, and initial sort (matches legacy Tooling Coverage table). */
export const DEFAULT_COLUMN_STATE = [
  { colId: 'name', width: 130, hide: false, pinned: null, sort: 'asc', flex: 1 },
  { colId: 'department', width: 220, hide: false, pinned: null, sort: null },
  { colId: 'numberOfEndpoints', width: 130, hide: true, pinned: null, sort: null },
  { colId: 'endpoints', width: 240, hide: false, pinned: null, sort: null, flex: 2 },
  { colId: 'attributionStatus', width: 150, hide: false, pinned: null, sort: null },
  { colId: 'identityAndAccess', width: 110, hide: false, pinned: null, sort: null },
  { colId: 'collaboration', width: 150, hide: false, pinned: null, sort: null },
  { colId: 'securityTraining', width: 110, hide: false, pinned: null, sort: null },
  { colId: 'endpointManagement', width: 110, hide: false, pinned: null, sort: null },
  { colId: 'endpointSecurity', width: 110, hide: false, pinned: null, sort: null },
  { colId: 'vulnerabilityManagement', width: 130, hide: false, pinned: null, sort: null },
];

// AG Grid theme configuration
export const customTheme = agGridAmpDarkTheme;
export const findingsTheme = agGridAmpDarkTheme;

export const COLUMN_STATE_STORAGE_KEYS = {
  server: 'usersInsightsModalGridColumnStateServer',
} as const;

export type ConnectorsType = {
    count: number;
    type: string;
    id: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    displayValue: string;
    status: ConnectorStatus;
    credentialStatus?: CredentialStatus;
    lastRunTime?: string;
    pid?: string | null | undefined;
    provider: ProviderDto;
};

export type AssetProviderType = {
    id: string;
    count: number;
    type: string;
    name: string;
    domains: string[];
    logo: string;
    status: ConnectorStatus;
    credentialStatus?: CredentialStatus;
    trainingCount?: number;
    completedTrainingCount?: number;
    lastRunTime?: string;
};

export type DomainColumn = {
    columnIcon: string;
    columnTitle?: string;
    columnWidth?: string;
};

export type DomainToColumnMap = {
    [columnName: string]: DomainColumn;
};

export type AssetsType = UserCoverageSummary['assets'][number];

export interface GridContext {
  tenantSettings: AmpSettingsMap | undefined;
  providerNameIntegrationMap: { [providerName: string]: any };
  isLoading: boolean;
  allConnectors: { [cid: string]: ConnectorDto & { provider: ProviderDto } };
  connectorDataHook: any;
  t: (key: string) => string;
}

export interface AgGridToolingComplianceProps {
  title?: string;
  defaultFilter?: Record<string, any>;
  tableBodyHeight?: string;
  emptyStateDescription?: string;
}

export const DEFAULT_COL_DEF = {
  minWidth: 50,
  resizable: true,
  sortable: false,
  filter: false,
  floatingFilter: false,
  suppressHeaderContextMenu: false,
  suppressHeaderMenuButton: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

export const GRID_CONFIG = {
  rowHeight: 40,
  headerHeight: 40,
  cacheBlockSize: 25,
  defaultHeight: '37.5rem',
  minHeight: '25rem',
  paginationPageSize: 50,
  sideBar: {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivots: true,
          suppressPivotMode: true,
          suppressRowGroups: true,
          suppressValues: true,
        },
      },
    ],
    hiddenByDefault: false,
    position: 'left',
  } as unknown as SideBarDef,
} as const;

export const INPUT_STYLES = {
  quickFilter: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '0.25rem',
    border: '1px solid #ccc',
    fontSize: '1rem',
  }
} as const;

export const FILTER_STYLES = {
  container: {
    padding: '1rem',
    minWidth: '17.5rem',
    maxHeight: '25rem',
    overflowY: 'auto' as const,
    background: 'var(--ag-popup-background-color)',
    borderRadius: 8,
    border: '1px solid var(--ag-border-color)',
  },
  section: {
    marginBottom: '1rem',
  },
  sectionHeading: {
    margin: '0 0 0.625rem 0',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--ag-foreground-color)',
    borderBottom: '1px solid var(--ag-border-color)',
    paddingBottom: 5,
  },
  fieldGroup: {
    marginBottom: '0.625rem',
  },
  label: {
    display: 'block' as const,
    marginBottom: '0.25rem',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--ag-secondary-foreground-color)',
  },
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid var(--ag-input-border-color)',
    borderRadius: 6,
    fontSize: 14,
    background: 'var(--ag-background-color)',
    color: 'var(--ag-foreground-color)',
    fontWeight: 500,
    transition: 'border 0.2s',
  },
  resetButtonContainer: {
    marginTop: '1rem',
    paddingTop: '0.625rem',
    borderTop: '1px solid var(--ag-border-color)',
  },
  resetButton: {
    padding: '0.5rem 1rem',
    width: '100%',
    background: 'var(--ag-background-color)',
    color: 'var(--ag-foreground-color)',
    border: '1px solid var(--ag-input-border-color)',
    borderRadius: 6,
    cursor: 'pointer' as const,
    fontWeight: 600,
    fontSize: 14,
    transition: 'border 0.2s',
  },
  focusBorder: '1.5px solid var(--ag-input-focus-border-color)',
  defaultBorder: '1px solid var(--ag-input-border-color)',
  selectedRowBackground: 'var(--ag-selected-row-background-color)',

  // Department multi-select filter
  deptHeading: {
    margin: '0 0 0.6rem 0',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'var(--ag-secondary-foreground-color)',
  },
  deptSearchWrap: {
    position: 'relative' as const,
    marginBottom: '0.5rem',
  },
  deptSearchInput: {
    width: '100%',
    padding: '0.4rem 0.65rem',
    border: '1px solid var(--ag-input-border-color)',
    borderRadius: 6,
    fontSize: 13,
    background: 'var(--ag-background-color)',
    color: 'var(--ag-foreground-color)',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  deptDropdown: {
    maxHeight: '11rem',
    overflowY: 'auto' as const,
    background: 'var(--ag-popup-background-color)',
    marginBottom: '0.5rem',
  },
  deptItem: {
    padding: '0.35rem 0.7rem',
    cursor: 'pointer' as const,
    fontSize: 13,
    color: 'var(--ag-foreground-color)',
    userSelect: 'none' as const,
    background: 'var(--ag-background-color)',
    border: '1px solid transparent',
    borderRadius: 4,
    margin: '0.1rem 0.25rem',
  },
  deptItemHover: {
    border: '1px solid rgba(124,58,237,0.7)',
    background: 'var(--ag-background-color)',
  },
  deptEmptyMsg: {
    padding: '0.35rem 0.7rem',
    fontSize: 12,
    fontStyle: 'italic' as const,
    color: 'var(--ag-secondary-foreground-color)',
  },
  deptChipsLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: 'var(--ag-secondary-foreground-color)',
    marginBottom: '0.3rem',
  },
  deptChipsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.3rem',
    marginBottom: '0.6rem',
  },
  deptChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.15rem 0.5rem 0.15rem 0.6rem',
    borderRadius: 12,
    background: 'rgba(124,58,237,0.18)',
    border: '1px solid rgba(124,58,237,0.5)',
    color: 'var(--ag-foreground-color)',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
    maxWidth: '14rem',
    overflow: 'hidden',
    whiteSpace: 'nowrap' as const,
    textOverflow: 'ellipsis',
  },
  deptChipRemove: {
    background: 'none',
    border: 'none',
    cursor: 'pointer' as const,
    padding: '0 0 0 0.1rem',
    fontSize: 14,
    lineHeight: 1,
    color: 'var(--ag-secondary-foreground-color)',
    flexShrink: 0,
  },
  deptDivider: {
    borderTop: '1px solid var(--ag-border-color)',
    margin: '0.5rem 0',
  },
  deptApplyBtn: {
    width: '100%',
    padding: '0.45rem 1rem',
    background: '#9975ff',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer' as const,
    fontWeight: 600,
    fontSize: 13,
    marginBottom: '0.4rem',
  },
} as const;